import type { JsonObject, TimestampMillis } from './common';

export const LICENSE_STATUSES = ['unused', 'active', 'expired', 'suspended', 'revoked'] as const;

export type LicenseStatus = (typeof LICENSE_STATUSES)[number];

export const LICENSE_TYPES = ['trial', 'standard', 'subscription', 'site', 'node'] as const;

export type LicenseType = (typeof LICENSE_TYPES)[number];

export const LICENSE_ACTIVATION_STATUSES = ['verified', 'pending', 'rejected'] as const;

export type LicenseActivationStatus = (typeof LICENSE_ACTIVATION_STATUSES)[number];

export const MAX_BULK_LICENSE_QUANTITY = 500;

export const MAX_LICENSE_MACHINES = 25;

export interface License {
  id: string;
  organization_id: string;
  script_id: string;
  customer_id: string | null;
  display_hint: string;
  license_type: LicenseType;
  status: LicenseStatus;
  max_activations: number;
  activated_count: number;
  max_simultaneous_sessions: number;
  starts_at: TimestampMillis | null;
  expires_at: TimestampMillis | null;
  last_activated_at: TimestampMillis | null;
  suspended_at: TimestampMillis | null;
  revoked_at: TimestampMillis | null;
  metadata: JsonObject;
  created_at: TimestampMillis;
  updated_at: TimestampMillis;
}

export interface LicenseIssueReceipt {
  license: License;
  secret: string;
  secret_issued_once: true;
}

export interface LicenseSession {
  id: string;
  license_id: string;
  project_id: string;
  machine_fingerprint_hash: string;
  client_version: string;
  started_at: TimestampMillis;
  last_heartbeat_at: TimestampMillis;
  expires_at: TimestampMillis;
  ended_at: TimestampMillis | null;
  checkpoint_completion_id: string | null;
  activation_status: LicenseActivationStatus;
}

export const LICENSE_STATUS_TRANSITIONS: Record<LicenseStatus, readonly LicenseStatus[]> = {
  unused: ['active', 'revoked', 'suspended', 'expired'],
  active: ['expired', 'suspended', 'revoked'],
  expired: ['revoked', 'suspended'],
  suspended: ['active', 'revoked', 'expired'],
  revoked: [],
};

export function isLicenseStatus(value: string): value is LicenseStatus {
  return (LICENSE_STATUSES as readonly string[]).includes(value);
}

export function canTransition(from: LicenseStatus, to: LicenseStatus): boolean {
  return LICENSE_STATUS_TRANSITIONS[from].includes(to);
}

export function effectiveStatus(
  license: Pick<License, 'status' | 'expires_at'>,
  reference: TimestampMillis,
): LicenseStatus {
  if (license.status === 'unused' || license.status === 'active') {
    if (license.expires_at !== null && license.expires_at <= reference) {
      return 'expired';
    }
  }
  return license.status;
}
